import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import en from '../i18n/locales/en.json'
import el from '../i18n/locales/el.json'
import nl from '../i18n/locales/nl.json'

const STORAGE_KEY = 'deverse-lang'

const dictionaries = { en, el, nl }

function getByPath(obj, path) {
  if (!obj || !path) return undefined
  return path.split('.').reduce((o, key) => (o && o[key] !== undefined ? o[key] : undefined), obj)
}

const LanguageContext = createContext(null)

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved && dictionaries[saved]) return saved
    } catch {
      /* ignore */
    }
    return 'en'
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language)
    } catch {
      /* ignore */
    }
    const htmlLang = { en: 'en', el: 'el', nl: 'nl' }[language] || 'en'
    document.documentElement.lang = htmlLang
  }, [language])

  const setLanguage = useCallback((code) => {
    if (dictionaries[code]) setLanguageState(code)
  }, [])

  const dict = dictionaries[language] || dictionaries.en

  const t = useCallback(
    (path, vars) => {
      let value = getByPath(dict, path)
      if (value === undefined) value = getByPath(dictionaries.en, path)
      if (typeof value !== 'string') return value
      if (vars && typeof value === 'string') {
        return value.replace(/\{\{(\w+)\}\}/g, (_, k) =>
          vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : `{{${k}}}`
        )
      }
      return value
    },
    [dict]
  )

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      t,
      dict,
    }),
    [language, setLanguage, t, dict]
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return ctx
}

export function useTranslation() {
  const { t, language, setLanguage, dict } = useLanguage()
  return { t, language, setLanguage, dict }
}
